<template>
    <CenteredContainer>
        <div class="pairing-view">
            <!-- Paired / diagnostics -->
            <div v-if="pairingMethod !== null" class="shell">
                <header class="header">
                    <h3 class="header-title">Terminal Diagnostics</h3>
                    <p class="header-sub">
                        Pairing: <span class="cap">{{ pairingMethod }}</span>
                        <span class="sep">•</span>
                        WebSocket:
                        <span :class="['status', isWebSocketConnected ? 'ok' : 'warn']">
                            <span :class="['dot', 'dot-inline', isWebSocketConnected ? 'green' : 'amber']"></span>
                            {{ isWebSocketConnected ? 'Connected' : 'Disconnected' }}
                        </span>
                        <span class="sep">•</span>
                        Internet:
                        <span :class="['status', isOnline ? 'ok' : 'bad']">
                            <span :class="['dot', 'dot-inline', isOnline ? 'green' : 'red']"></span>
                            {{ isOnline ? 'Online' : 'Offline' }}
                        </span>
                    </p>
                </header>

                <div class="grid">
                    <section class="panel">
                        <h5 class="panel-title">Identity</h5>
                        <dl class="kv">
                            <div><dt>Name</dt><dd>{{ terminalDetails?.name || '—' }}</dd></div>
                            <div><dt>Merchant ID</dt><dd class="mono">{{ merchantIdDisplay }}</dd></div>
                            <div>
                                <dt>Serial</dt>
                                <dd class="mono">
                                    {{ serial || '—' }}
                                </dd>
                            </div>
                        </dl>
                    </section>

                    <section class="panel panel-system">
                        <h5 class="panel-title">System</h5>
                        <dl class="kv">
                            <div><dt>Last Boot</dt><dd>{{ formattedLastUpdate }}</dd></div>
                            <div><dt>Display</dt><dd>{{ screenResolution }}</dd></div>
                            <div><dt>Orientation</dt><dd class="cap">{{ screenOrientation }}</dd></div>
                        </dl>
                        <button class="btn btn-md btn-outline-primary w-100" @click="checkUpdate" :disabled="checkingUpdate">
                            {{ checkingUpdate ? 'Checking…' : 'Check for Update' }}
                        </button>
                    </section>

                    <section class="panel">
                        <h5 class="panel-title">Network</h5>
                        <dl class="kv">
                            <div>
                                <dt>Internet</dt>
                                <dd>
                                    <span :class="['dot', 'dot-inline', isOnline ? 'green' : 'red']"></span>
                                    {{ isOnline ? 'Online' : 'Offline' }}
                                </dd>
                            </div>
                            <div>
                                <dt>WebSocket</dt>
                                <dd>
                                    <span :class="['dot', 'dot-inline', isWebSocketConnected ? 'green' : 'amber']"></span>
                                    {{ isWebSocketConnected ? 'Connected' : 'Disconnected' }}
                                </dd>
                            </div>
                        </dl>
                    </section>
                </div>

                <footer class="footer">
                    <button v-if="pairingMethod !== 'serial'" class="btn btn-lg btn-outline-primary footer-danger w-100" @click="confirmUnpair" :disabled="loading">
                        {{ loading ? 'Unpairing…' : 'Unpair Terminal' }}
                    </button>
                    <button class="btn btn-lg btn-primary w-100" @click="closeAndReturnToStart" :disabled="realodingPage">
                        Close
                    </button>
                </footer>
            </div>

            <!-- Not paired / pairing -->
            <div v-else class="shell">
                <header class="pairing-header">
                    <img src="@/assets/skytab-logo.svg" alt="SkyTab Logo" class="logo" />
                    <div class="pairing-header-text">
                        <h3 class="pairing-title">
                            <SvgIcon name="terminal" class="icon" width="1.2em" height="1.2em" /> Pair Terminal
                        </h3>
                        <p class="pairing-sub">
                            Link this kiosk to a location using the serial number, or use an override method if directed by support.
                        </p>
                    </div>
                </header>

                <div class="grid pairing-grid">
                    <section v-if="serial && !serial.startsWith('DEMO')" class="panel qr">
                        <h5 class="panel-title">Lighthouse Setup</h5>
                        <div class="qr-body">
                            <img src="@/assets/qr-code-launch-control.png" alt="QR Code" class="qr-code" />
                            <div class="qr-text">
                                <p>Scan this code and complete the <strong>Set Up Kiosk</strong> module in Lighthouse.</p>
                                <p class="muted">After setup, tap <strong>Serial Number</strong> to link this terminal.</p>
                            </div>
                        </div>
                    </section>

                    <section class="panel actions">
                        <h5 class="panel-title">Link Terminal</h5>

                        <template v-if="!showPinRegister && !showMidRegister">
                            <button class="btn btn-lg btn-outline-primary" @click="registerTerminalSerial" :disabled="loading">
                                <span v-if="!loading">Serial Number: {{ serial || '—' }}</span>
                                <span v-else class="loading-inline">
                                    <img src="@/assets/spinner-sm.png" alt="Loading" class="spinner-sm rotating" />
                                    Working…
                                </span>
                            </button>

                            <button class="btn btn-lg btn-outline-primary mt-3" @click="manuallyRegisterPin" v-if="!loading">
                                Enter PIN Code (Override)
                            </button>

                            <button class="btn btn-lg btn-outline-primary mt-3" @click="registerByMid" v-if="!loading && serial && serial.startsWith('DEMO')">
                                Merchant ID (Demo Mode)
                            </button>
                        </template>

                        <template v-else-if="showMidRegister">
                            <p class="muted mb-3">Enter the Merchant ID to start demo mode.</p>
                            <input type="text" v-model="mid" placeholder="Enter MID" class="w-100" />
                            <div class="row mt-3">
                                <div class="col-6">
                                    <button class="btn btn-lg btn-outline-primary" @click="cancelMidRegister">
                                        Cancel
                                    </button>
                                </div>
                                <div class="col-6">
                                    <button class="btn btn-lg btn-primary" @click="registerTerminalMid" :disabled="!isMidValid">
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </template>

                        <template v-else>
                            <p class="muted mb-3">Enter the 6-digit PIN provided by support.</p>
                            <div class="pin-inputs" aria-label="PIN input">
                                <input v-for="(digit, index) in pinDigits" :key="index" type="text" maxlength="1"
                                    inputmode="numeric" pattern="[0-9]*" v-model="pinDigits[index]"
                                    @input="onPinInput(index, $event)" @keydown="onPinKeydown(index, $event)"
                                    @paste="onPinPaste($event)" ref="pinInput" class="pin-input" aria-label="PIN Digit" />
                            </div>
                            <div class="row mt-3">
                                <div class="col-6">
                                    <button class="btn btn-lg btn-outline-primary" @click="cancelPinRegister">
                                        Cancel
                                    </button>
                                </div>
                                <div class="col-6">
                                    <button class="btn btn-lg btn-primary" @click="registerTerminalPin" :disabled="!isPinValid">
                                        Link
                                    </button>
                                </div>
                            </div>
                        </template>
                    </section>
                </div>
            </div>

            <!-- global fixed-position info (styled in public/v3.css) -->
            <div class="version-number">{{ feVersion }}</div>
            <div class="kiosk-serial" v-if="serial">{{ serial }}</div>
        </div>
    </CenteredContainer>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import CenteredContainer from "@/components/global/CenteredContainer.vue";
import { checkUpdateAvailable, performReload, getLastReloadTime } from "@/utils/scheduleDailyRestart";

export default {
    name: "PairingView",
    components: {
        CenteredContainer,
    },
    emits: ['pairingComplete', 'close'],
    data() {
        return {
            loading: false,
            realodingPage: false,
            showPinRegister: false,
            pinDigits: Array(6).fill(''),
            showMidRegister: false,
            mid: '',
            checkingUpdate: false,
            lastUpdateTimestamp: 0,
        };
    },
    computed: {
        ...mapGetters('terminal', ['serial', 'terminalPaired', 'terminalDetails', 'errorMessage', 'pairingMethod', 'previewMid', 'printerIp', 'printerStatus', 'printerTestId', 'deviceId']),
        ...mapGetters('location', ['mid']),
        ...mapGetters(["feVersion", "isWebSocketConnected", "screenOrientation"]),
        merchantIdDisplay() {
            return this.mid || this.previewMid || '—';
        },
        formattedLastUpdate() {
            if (!this.lastUpdateTimestamp) return '—';
            const d = new Date(this.lastUpdateTimestamp);
            return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        },
        screenResolution() {
            return `${window.screen.width}×${window.screen.height}`;
        },
        isOnline() {
            return navigator.onLine;
        },
        isPinValid() {
            return this.pinDigits.every(digit => /^\d$/.test(digit));
        },
        isMidValid() {
            return this.mid.trim().length > 0;
        },
    },
    watch: {
        errorMessage(newVal) {
            if (newVal) {
                this.$store.dispatch("dialog/openDialog", {
                    title: "Error",
                    message: newVal,
                    showCancel: false,
                    onConfirm: async () => { }
                });
            }
        },
        showPinRegister(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    if (this.$refs.pinInput && this.$refs.pinInput[0]) {
                        this.$refs.pinInput[0].focus();
                    }
                });
            }
        },
    },
    methods: {
        ...mapActions('terminal', ['registerTerminalSerial', 'deregisterTerminal']),
        async checkUpdate() {
            this.checkingUpdate = true;
            try {
                const result = await checkUpdateAvailable();
                if (result.available) {
                    this.$store.dispatch("dialog/openDialog", {
                        title: "Update Available",
                        message: "A new version is available. Reload now?",
                        showCancel: true,
                        onConfirm: async () => {
                            performReload();
                        }
                    });
                } else {
                    this.$store.dispatch("dialog/openDialog", {
                        title: "Up to Date",
                        message: "You're running the latest version.",
                        showCancel: false,
                        onConfirm: async () => { }
                    });
                }
            } catch (e) {
                console.error(e);
                this.$store.dispatch("dialog/openDialog", {
                    title: "Error",
                    message: "Failed to check for updates.",
                    showCancel: false,
                    onConfirm: async () => { }
                });
            } finally {
                this.checkingUpdate = false;
                this.refreshLastUpdate();
            }
        },
        refreshLastUpdate() {
            this.lastUpdateTimestamp = getLastReloadTime();
        },
        onPinKeydown(index, event) {
            if (event.key === 'Backspace') {
                event.preventDefault();
                this.pinDigits[index] = '';
                if (index > 0) {
                    this.$nextTick(() => {
                        this.$refs.pinInput[index - 1].focus();
                    });
                }
            }
        },
        onPinInput(index, event) {
            const value = event.target.value.replace(/\D/g, '');
            this.pinDigits[index] = value.slice(-1);

            if (value && index < 5) {
                this.$nextTick(() => {
                    this.$refs.pinInput[index + 1].focus();
                });
            }

            if (this.pinDigits.every(digit => /^\d$/.test(digit))) {
                this.registerTerminalPin();
            }
        },
        onPinPaste(event) {
            event.preventDefault();
            const pastedText = event.clipboardData.getData('text');
            const digits = pastedText.replace(/\D/g, '').slice(0, 6).split('');

            digits.forEach((digit, i) => {
                this.pinDigits[i] = digit;
            });

            const nextEmptyIndex = this.pinDigits.findIndex(d => d === '');
            if (nextEmptyIndex !== -1) {
                this.$nextTick(() => {
                    this.$refs.pinInput[nextEmptyIndex].focus();
                });
            } else {
                this.$nextTick(() => {
                    this.$refs.pinInput[5].blur();
                });
            }
        },
        async registerTerminalPin() {
            this.loading = true;
            try {
                const pinCode = this.pinDigits.join('');
                await this.$store.dispatch('terminal/registerTerminal', { pin: pinCode });
            } catch (error) {
                console.error(error);
            } finally {
                this.loading = false;
                this.pinDigits = Array(6).fill('');
            }
        },
        async registerTerminalSerial() {
            this.loading = true;
            try {
                await this.$store.dispatch('terminal/registerTerminalSerial');
            } catch (error) {
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        manuallyRegisterPin() {
            this.showPinRegister = true;
        },
        async confirmUnpair() {
            let message = "Are you sure you want to unpair the terminal? This can not be undone.";
            if (this.pairingMethod === 'pin') {
                // they will need to get a new pin from support to re-pair this terminal
                message += " You will need to get a new PIN from support to re-pair this terminal.";
            }

            this.$store.dispatch("dialog/openDialog", {
                title: "Unlink?",
                message,
                showCancel: true,
                onConfirm: async () => {
                    this.loading = true;
                    try {
                        await this.deregisterTerminal();
                    } catch (error) {
                        console.error(error);
                    } finally {
                        this.loading = false;
                    }
                }
            });
        },
        closeAndReturnToStart() {
            this.realodingPage = true;
            this.$emit('close');
        },
        cancelPinRegister() {
            this.showPinRegister = false;
            this.pinDigits = Array(6).fill('');
        },
        registerByMid() {
            this.showMidRegister = true;
        },
        cancelMidRegister() {
            this.showMidRegister = false;
            this.mid = '';
        },
        async registerTerminalMid() {
            this.loading = true;
            try {
                // Dispatch the action with the MID
                const response = await this.$store.dispatch('terminal/registerTerminalByMid', this.mid);
                if (response.success) {
                    this.$emit('close');
                } else {
                    this.$store.dispatch("dialog/openDialog", {
                        title: "Error",
                        message: response.error,
                        showCancel: false,
                        onConfirm: async () => { }
                    });
                }

            } catch (error) {
                console.error(error);
                this.$store.dispatch("dialog/openDialog", {
                    title: "Error",
                    message: error.message,
                    showCancel: false,
                    onConfirm: async () => { }
                });
            } finally {
                this.loading = false;
                this.mid = '';
            }
        },
    },
    mounted() {
        this.refreshLastUpdate();
        // eslint-disable-next-line no-undef
        if (this.printerIp && typeof Shift4Kiosk !== 'undefined') {
            // eslint-disable-next-line no-undef
            Shift4Kiosk.onPrinterTestSuccess = (testResult) => {
                if (testResult.id === this.printerTestId) {
                    this.$store.commit('terminal/SET_PRINTER_STATUS', 'online');
                }
            };

            // eslint-disable-next-line no-undef
            Shift4Kiosk.onPrinterTestError = (testError) => {
                if (testError.id === this.printerTestId) {
                    this.$store.commit('terminal/SET_PRINTER_STATUS', 'offline');
                }
            };
        }
    },
    beforeUnmount() {
        // eslint-disable-next-line no-undef
        if (typeof Shift4Kiosk !== 'undefined') {
            // eslint-disable-next-line no-undef
            Shift4Kiosk.onPrinterTestSuccess = null;
            // eslint-disable-next-line no-undef
            Shift4Kiosk.onPrinterTestError = null;
        }
    },
};
</script>

<style scoped>
.pairing-view {
    width: 100vw;
    height: 100vh;
    padding: 1.5rem;
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.shell {
    width: 100%;
    max-width: 100%;
    max-height: 100%;
    background: var(--primary-white);
    padding: 1.75rem;
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.header {
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 0.125rem solid var(--grey-outline);
    flex-shrink: 0;
}

.header-title {
    margin: 0 0 0.5rem 0;
    font-size: clamp(1.5rem, 4vw, 2rem);
    font-weight: 700;
    color: var(--primary-black);
    letter-spacing: -0.02em;
    line-height: 1.2;
}

.header-sub {
    margin: 0;
    font-size: clamp(1rem, 2.5vw, 1.125rem);
    color: var(--grey-medium);
    font-weight: 400;
    line-height: 1.4;
}

.sep {
    margin: 0 0.75rem;
    color: var(--grey-outline);
}

.status.ok { color: #46A82E; }
.status.warn { color: #F59E0B; }
.status.bad { color: var(--status-error); }

.grid {
    display: flex;
    flex-wrap: wrap;
    margin: -0.5rem;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

.grid > * {
    flex: 1 1 calc(33.333% - 1rem);
    min-width: 0;
    margin: 0.5rem;
}


.panel {
    background: var(--primary-white);
    border: 0.125rem solid var(--grey-outline);
    border-radius: var(--border-radius-lg);
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    min-height: 0;
    max-height: 100%;
    overflow: hidden;
}

.panel-system .btn {
    margin-top: 1rem;
    flex-shrink: 0;
}

.panel.qr {
    flex: 1 1 25rem;
}

.panel.actions {
    flex: 1 1 25rem;
}

.panel-title {
    margin: 0 0 0.875rem 0;
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--primary-black);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    flex-shrink: 0;
}

.kv {
    margin: 0;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.kv > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.625rem 0;
    border-bottom: 0.125rem solid var(--grey-outline);
}

.kv > div:last-child {
    border-bottom: none;
}

.kv dt {
    font-weight: 500;
    color: var(--grey-medium);
    font-size: clamp(0.875rem, 2vw, 1rem);
}

.kv dd {
    margin: 0;
    display: flex;
    align-items: center;
    font-weight: 600;
    color: var(--primary-black);
    font-size: clamp(0.875rem, 2vw, 1rem);
    text-align: right;
}

.kv dd > *:not(:first-child) {
    margin-left: 0.625rem;
}

.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-weight: 700;
}

.cap {
    text-transform: capitalize;
}

.muted {
    color: var(--grey-medium);
    margin: 0;
}

.dot {
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 100%;
    flex-shrink: 0;
}

.dot-inline {
    display: inline-block;
    vertical-align: middle;
    margin-right: 0.625rem;
}

.dot.green { background: #46A82E; }
.dot.red { background: var(--status-error); }
.dot.amber { background: #F59E0B; }

.footer {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 0.125rem solid var(--grey-outline);
    flex-shrink: 0;
}

.footer .btn {
    width: 100%;
}

.footer .btn + .btn {
    margin-top: 0.75rem;
}

.footer-danger {
    border-color: var(--status-error) !important;
    color: var(--status-error) !important;
}

.pairing-header {
    display: flex;
    align-items: center;
    margin-bottom: 2.5rem;
}

.pairing-header > *:not(:first-child) {
    margin-left: 2rem;
}

.logo {
    display: block;
    width: 10rem;
    height: 10rem;
}

.pin-inputs {
    display: flex;
    justify-content: center;
    margin-top: 1rem;
}

.pin-inputs > *:not(:first-child) {
    margin-left: 0.75rem;
}

.pin-input {
    width: 4.5rem;
    height: 5.5rem;
    text-align: center;
    font-size: 3rem;
    border: 0.25rem solid var(--grey-focus);
    border-radius: var(--border-radius-sm);
    background: var(--primary-white);
    color: var(--primary-black);
    font-weight: 700;
}

.pin-input:focus {
    border-color: var(--brand-color);
    outline: none;
}

.qr-body {
    display: flex;
    align-items: center;
    padding: 1rem 0;
}

.qr-body > *:not(:first-child) {
    margin-left: 2rem;
}

.qr-code {
    width: 12rem;
    height: 12rem;
    flex-shrink: 0;
}

.qr-text p {
    margin: 0 0 1rem 0;
    font-size: 1.5rem;
}

.loading-inline {
    display: inline-flex;
    align-items: center;
}

.loading-inline > *:not(:first-child) {
    margin-left: 1rem;
}

.icon {
    margin-right: 0.75rem;
    position: relative;
    top: -0.1rem;
}

@media (max-width: 1100px) {
    .grid > * {
        flex: 1 1 calc(50% - 1rem);
    }
}

@media (max-width: 768px) {
    .grid > * {
        flex: 1 1 100%;
    }

    .pairing-view {
        padding: 1rem;
    }

    .shell {
        padding: 1.25rem;
    }

    .qr-body {
        flex-direction: column;
        text-align: center;
    }

    .qr-body > *:not(:first-child) {
        margin-left: 0;
        margin-top: 1.5rem;
    }
}
</style>